/** Shared loading, empty and error presentations, so every list page behaves alike. */

import { AlertCircle, Inbox, Loader2, Lock, RotateCw } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

/**
 * An indeterminate wait.
 *
 * Reserved for whole-route transitions, where nothing is yet known about the shape of
 * what is coming. Anywhere the layout *is* known — a table, a card grid — prefer a
 * skeleton, which holds the space instead of collapsing it and then pushing the page
 * around when data lands.
 */
export function LoadingState({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-3 py-16', className)}
      role="status"
      aria-live="polite"
    >
      <span className="relative flex size-8 items-center justify-center">
        {/* An expanding halo behind the spinner: motion at two rates reads as
            activity rather than as a frozen graphic. */}
        <span className="absolute inset-0 animate-pulse-ring rounded-full bg-primary/20" aria-hidden="true" />
        <Loader2 className="size-5 animate-spin text-primary" aria-hidden="true" />
      </span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  );
}

/** Card-grid placeholder, matching the geometry of the grid it stands in for. */
export function CardGridSkeleton({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div
      className={cn('grid gap-4 sm:grid-cols-2 xl:grid-cols-4', className)}
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="space-y-3 rounded-lg border border-border bg-card p-5"
          style={{ animationDelay: `${index * 60}ms` }}
        >
          <Skeleton className="size-9 rounded-lg" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon: Icon = Inbox,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 px-6 py-16 text-center',
        className,
      )}
    >
      {/* Concentric rings put the icon at the centre of something rather than
          leaving it floating in dead space. */}
      <span className="relative flex size-14 items-center justify-center rounded-full bg-muted/60 ring-8 ring-muted/25">
        <Icon className="size-6 text-muted-foreground" aria-hidden="true" />
      </span>

      <div className="max-w-sm space-y-1.5">
        <p className="text-md font-semibold">{title}</p>
        {description ? (
          <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
        ) : null}
      </div>

      {action}
    </div>
  );
}

/**
 * Presents a failed request.
 *
 * The distinction that matters to a user is *why* they cannot see something: a module
 * their organisation has not enabled reads very differently from one they lack the
 * permission for, and both read differently from a server fault. Retry is offered only
 * for the last of those — retrying a permission denial just repeats it.
 */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const apiError = error instanceof ApiError ? error : null;

  const isAccess =
    apiError?.code === 'permission_denied' ||
    apiError?.code === 'feature_disabled' ||
    apiError?.code === 'feature_read_only';

  const Icon = isAccess ? Lock : AlertCircle;

  const message =
    apiError?.message ??
    (error instanceof Error ? error.message : 'Something went wrong. Please try again.');

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 px-6 py-16 text-center',
        className,
      )}
      role="alert"
    >
      <span
        className={cn(
          'flex size-14 items-center justify-center rounded-full ring-8',
          isAccess ? 'bg-muted/60 ring-muted/25' : 'bg-destructive/10 ring-destructive/5',
        )}
      >
        <Icon
          className={cn('size-6', isAccess ? 'text-muted-foreground' : 'text-destructive')}
          aria-hidden="true"
        />
      </span>

      <div className="max-w-md space-y-1.5">
        <p className="text-md font-semibold">
          {isAccess ? 'Not available to you' : 'Something went wrong'}
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">{message}</p>

        {apiError?.requestId ? (
          <p className="pt-1 text-xs text-muted-foreground">
            Reference:{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-2xs">
              {apiError.requestId}
            </code>
          </p>
        ) : null}
      </div>

      {onRetry && !isAccess ? (
        <Button variant="secondary" size="sm" iconMotion="rotate" onClick={onRetry}>
          <RotateCw aria-hidden="true" />
          Try again
        </Button>
      ) : null}
    </div>
  );
}

/**
 * A banner for a module frozen read-only by policy.
 *
 * Used in place of hiding the controls: a user should see that a capability exists and
 * is currently locked, rather than watch it vanish with no explanation.
 */
export function ReadOnlyNotice({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex items-start gap-2.5 rounded-lg border border-warning/25 bg-warning/[0.08] px-3.5 py-2.5"
      role="status"
    >
      <Lock className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
      <p className="text-sm leading-relaxed text-foreground/80">{children}</p>
    </div>
  );
}
